import { gql, type TypedDocumentNode } from '@apollo/client';
import type { ComponentProps } from 'react';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { WidgetChip } from '@sorare/core/src/atoms/ui/Chip/WidgetChip/index.tsx';
import { ANY_SPORT_PLAY_MISSIONS_CATEGORY_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useEnableSorare27Nav } from '@sorare/core/src/hooks/useEnableSorare27Nav';

import { DumbPlayMissionWidget } from '#src/components/missions/DumbPlayMissionWidget/index.tsx';

import type { PlayMissionsButton_currentUser } from './__generated__/index.graphql.ts';

type Props = {
  rarity: Rarity;
  currentUser?: PlayMissionsButton_currentUser | null;
} & Pick<ComponentProps<typeof DumbPlayMissionWidget>, 'size'>;

export const PlayMissionsButton = ({
  rarity,
  currentUser,
  size = 5,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const enableSorare27Nav = useEnableSorare27Nav();

  if (!enableSorare27Nav) {
    return null;
  }

  const hasUnclaimedTasks = (currentUser?.myPlayUnclaimedTasksCount ?? 0) > 0;

  return (
    <DumbPlayMissionWidget
      chip={hasUnclaimedTasks ? <WidgetChip state="reward" /> : undefined}
      size={size}
      to={generateSportContextPath(ANY_SPORT_PLAY_MISSIONS_CATEGORY_RARITY, {
        params: { category: 'play', rarity },
      })}
    />
  );
};

PlayMissionsButton.fragments = {
  currentUser: gql`
    fragment PlayMissionsButton_currentUser on CurrentUser {
      slug
      myPlayUnclaimedTasksCount: myUnclaimedTasksCount(
        sport: $sport
        group: "play"
      )
    }
  ` as TypedDocumentNode<PlayMissionsButton_currentUser>,
};
